"use client";

import { useUser } from "@clerk/nextjs";
import { useCallback, useEffect, useRef } from "react";

import {
  isClerkUserReloadDue,
  readStoredClerkUserReloadAt,
  writeStoredClerkUserReloadAt,
} from "@/lib/daily-clerk-user-reload";

export default function DailyClerkUserReload() {
  const { isLoaded, isSignedIn, user } = useUser();
  const inFlightUserIdRef = useRef<string | null>(null);
  const memoryReloadedAtByUserRef = useRef<Record<string, number>>({});

  const reloadIfDue = useCallback(async () => {
    if (!isLoaded || !isSignedIn || !user) return;
    if (inFlightUserIdRef.current === user.id) return;

    const storedReloadedAt = readStoredClerkUserReloadAt(user.id);
    const memoryReloadedAt = memoryReloadedAtByUserRef.current[user.id] ?? null;
    const lastReloadedAt =
      storedReloadedAt === null && memoryReloadedAt === null
        ? null
        : Math.max(storedReloadedAt ?? 0, memoryReloadedAt ?? 0);

    if (!isClerkUserReloadDue(lastReloadedAt)) return;

    inFlightUserIdRef.current = user.id;

    try {
      await user.reload();

      const reloadedAt = Date.now();
      memoryReloadedAtByUserRef.current[user.id] = reloadedAt;
      writeStoredClerkUserReloadAt(user.id, reloadedAt);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[Clerk] Daily user reload failed:", error);
      }
    } finally {
      if (inFlightUserIdRef.current === user.id) {
        inFlightUserIdRef.current = null;
      }
    }
  }, [isLoaded, isSignedIn, user]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return;

    void reloadIfDue();

    const handleFocus = () => {
      void reloadIfDue();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void reloadIfDue();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isLoaded, isSignedIn, user, reloadIfDue]);

  return null;
}
